import React from 'react';
import { Header } from '../components/Header';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import { ArrowLeftIcon, TrendingUpIcon, ClockIcon, CheckCircleIcon, AlertTriangleIcon, FlameIcon, AlertOctagonIcon, HelpCircleIcon, MessageSquareIcon, ShieldIcon, DownloadIcon } from 'lucide-react';
interface AdminScreenProps {
  onBack: () => void;
  onLogout: () => void;
  onNavigateCases: () => void;
}
export function AdminScreen({
  onBack,
  onLogout,
  onNavigateCases
}: AdminScreenProps) {
  // Mock data for the admin dashboard
  const progress = {
    total: 2847,
    reviewed: 892,
    burnRate: 145,
    estCompletion: 'Oct 28, 2023'
  };
  const teamActivity = [{
    name: 'Sarah Chen',
    role: 'Lead Reviewer',
    reviewed: 412,
    hot: 28,
    critical: 64,
    tbd: 12,
    avgTime: '42s',
    lastActive: 'Just now',
    status: 'active'
  }, {
    name: 'Michael Torres',
    role: 'Reviewer',
    reviewed: 308,
    hot: 14,
    critical: 42,
    tbd: 22,
    avgTime: '58s',
    lastActive: '2 hrs ago',
    status: 'active'
  }, {
    name: 'Jessica Wong',
    role: 'Reviewer',
    reviewed: 172,
    hot: 5,
    critical: 18,
    tbd: 4,
    avgTime: '1m 15s',
    lastActive: 'Yesterday',
    status: 'warning' // Inactive > 24h
  }];
  const qualitySignals = {
    avgTbdRate: '4.2%',
    totalComments: 156,
    flaggedForQa: 24,
    privileged: 89
  };
  const pctComplete = Math.round(progress.reviewed / progress.total * 100);
  return <div className="min-h-screen flex flex-col bg-navy" data-id="element-434" data-mp="3atap97ol4ub2">
      <Header breadcrumbs={[{
      label: 'Cases',
      onClick: onNavigateCases
    }, {
      label: 'Smith v. Acme Corp',
      onClick: onBack
    }, {
      label: 'Admin'
    }]} onLogout={onLogout} data-id="element-435" data-mp="3aqohb6xs8csb" />

      <main className="flex-1 overflow-y-auto p-8" data-id="element-436" data-mp="c30q1u2bj4mr">
        <div className="max-w-6xl mx-auto space-y-8" data-id="element-437" data-mp="c4lnbrg0p1ob">
          {/* Navigation & Header */}
          <div data-id="element-438" data-mp="lxpuagrg3d2w">
            <button onClick={onBack} className="flex items-center text-sm text-gray-400 hover:text-white mb-6 transition-colors" data-id="element-439" data-mp="lw504s91lw5k">
              <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-440" data-mp="byqlmy6ojhfw" />
              Back to Case Overview
            </button>

            <div className="flex items-start justify-between" data-id="element-441" data-mp="80sw1u8zytbz">
              <div data-id="element-442" data-mp="30oncqebz2wb">
                <h1 className="text-3xl font-serif text-white mb-2" data-id="element-443" data-mp="317zfqhaux2f">
                  Admin Dashboard
                </h1>
                <p className="text-gray-400" data-id="element-444" data-mp="3uchplyb1qloi">
                  Team performance, review progress, and quality oversight.
                </p>
              </div>
              <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-300 bg-navy border border-navy-lighter rounded-lg hover:bg-navy-hover hover:text-white transition-colors" data-id="element-445" data-mp="3khfyiwx7n9ja">
                <DownloadIcon className="w-4 h-4" data-id="element-446" data-mp="3bfynvu2ko01z" />
                Export Audit Log
              </button>
            </div>
          </div>

          {/* 1. Review Progress */}
          <Card className="p-6" data-id="element-447" data-mp="1vvpkodj7ht7j">
            <h2 className="text-lg font-serif text-white mb-6" data-id="element-448" data-mp="1vuo0njk7pwdo">
              Review Progress
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" data-id="element-449" data-mp="1zvzq9p336hlv">
              <div data-id="element-450" data-mp="24wni2cz532h5">
                <div className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-1" data-id="element-451" data-mp="24v2x9f2mmxwh">
                  Total Documents
                </div>
                <div className="text-3xl font-serif text-white" data-id="element-452" data-mp="1c7q8qzcwog0j">
                  {progress.total.toLocaleString()}
                </div>
              </div>
              <div data-id="element-453" data-mp="1h5rwvyp6v5iv">
                <div className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-1" data-id="element-454" data-mp="1lyuq25q20cyj">
                  Reviewed
                </div>
                <div className="text-3xl font-serif text-green-400" data-id="element-455" data-mp="oie1w651wk1e">
                  {progress.reviewed.toLocaleString()}
                </div>
              </div>
              <div data-id="element-456" data-mp="2w3w4jnpomkes">
                <div className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-1" data-id="element-457" data-mp="2w5gypc8341c4">
                  Burn Rate
                </div>
                <div className="flex items-baseline gap-2" data-id="element-458" data-mp="34wzdpjtaob3m">
                  <div className="text-3xl font-serif text-white" data-id="element-459" data-mp="34yk6b3w1tjyu">
                    {progress.burnRate}
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-460" data-mp="304g22se63q1g">docs/day</div>
                </div>
              </div>
              <div data-id="element-461" data-mp="39wcaaw9ywwje">
                <div className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-1" data-id="element-462" data-mp="39wvhpj1zs2j6">
                  Est. Completion
                </div>
                <div className="text-3xl font-serif text-amber" data-id="element-463" data-mp="3ntr1a5fvdcdh">
                  {progress.estCompletion}
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div data-id="element-464" data-mp="1tjwyot6uftlk">
              <div className="flex justify-between text-sm mb-2" data-id="element-465" data-mp="1pg8r4ai7oy0b">
                <span className="text-gray-400" data-id="element-466" data-mp="1pi1v1i1fx44m">{pctComplete}% Complete</span>
                <span className="text-gray-500" data-id="element-467" data-mp="1fdlgqt3lzv2x">
                  {(progress.total - progress.reviewed).toLocaleString()}{' '}
                  remaining
                </span>
              </div>
              <div className="h-3 w-full bg-navy-lighter rounded-full overflow-hidden" data-id="element-468" data-mp="15lpjtld9eb0a">
                <div className="h-full bg-green-500 rounded-full transition-all duration-1000" style={{
                width: `${pctComplete}%`
              }} data-id="element-469" data-mp="1akk0agnr7nh8" />
              </div>
            </div>
          </Card>

          {/* 2. Team Activity */}
          <Card className="overflow-hidden" data-id="element-470" data-mp="moxzr9pw7rv6">
            <div className="p-6 border-b border-navy-lighter flex justify-between items-center" data-id="element-471" data-mp="rmpwch1hg25p">
              <h2 className="text-lg font-serif text-white" data-id="element-472" data-mp="2h1nwdz7nbf69">Team Activity</h2>
              <Badge variant="blue" data-id="element-473" data-mp="2gzkvul8va3na">
                {teamActivity.length} Active Reviewers
              </Badge>
            </div>

            <div className="overflow-x-auto" data-id="element-474" data-mp="2jlu3gfrk3lr1">
              <table className="w-full text-left border-collapse" data-id="element-475" data-mp="2jnn7dnasbrvc">
                <thead data-id="element-476" data-mp="2fk8qyvixp9hd">
                  <tr className="bg-navy-lighter/30 text-xs uppercase tracking-wider text-gray-400 border-b border-navy-lighter" data-id="element-477" data-mp="2fixco1rv4jc0">
                    <th className="px-6 py-4 font-semibold" data-id="element-478" data-mp="2an8lalpzy8qs">Reviewer</th>
                    <th className="px-6 py-4 font-semibold text-right" data-id="element-479" data-mp="25nvop99bpnb0">
                      Reviewed
                    </th>
                    <th className="px-6 py-4 font-semibold text-center" data-id="element-480" data-mp="33bvz5w8f1vub">
                      Classifications
                    </th>
                    <th className="px-6 py-4 font-semibold text-right" data-id="element-481" data-mp="2tjhx12dfen7m">
                      Avg Time
                    </th>
                    <th className="px-6 py-4 font-semibold" data-id="element-482" data-mp="2okng65z0r8q5">Last Active</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-navy-lighter" data-id="element-483" data-mp="3m2ngpl2k4sjs">
                  {teamActivity.map((member, i) => <tr key={i} className="hover:bg-navy-hover/50 transition-colors" data-id="element-484" data-mp="3ol0eeekh00sv" data-mpi={i}>
                      <td className="px-6 py-4" data-id="element-485" data-mp="3tkv6glfzewfi">
                        <div className="flex items-center gap-3" data-id="element-486" data-mp="2dsfnv422s5t">
                          <div className={`w-2 h-2 rounded-full ${member.status === 'active' ? 'bg-green-500' : 'bg-amber'}`} data-id="element-487" data-mp="2ch95nj876b0" />
                          <div data-id="element-488" data-mp="bdxkbxhv7cga">
                            <div className="text-white font-medium" data-id="element-489" data-mp="bcmfduconynl">
                              {member.name}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-490" data-mp="l5bd7mfb9e0g">
                              {member.role}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-491" data-mp="v4anf9fq5hpk">
                        <span className="text-lg font-serif text-white" data-id="element-492" data-mp="z39tixd6zkil">
                          {member.reviewed}
                        </span>
                      </td>
                      <td className="px-6 py-4" data-id="element-493" data-mp="1gdod9kfmyx9j">
                        <div className="flex items-center justify-center gap-3 text-xs" data-id="element-494" data-mp="1gcv309hgxpmn">
                          <div className="flex items-center gap-1 text-red-400" title="Hot" data-id="element-495" data-mp="1ubutlmojkelg">
                            <FlameIcon className="w-3.5 h-3.5" data-id="element-496" data-mp="11nql6tel5nf2" />
                            {member.hot}
                          </div>
                          <div className="flex items-center gap-1 text-orange-400" title="Critical" data-id="element-497" data-mp="wr8j61an436f">
                            <AlertOctagonIcon className="w-3.5 h-3.5" data-id="element-498" data-mp="16mtgvn8oehad" />
                            {member.critical}
                          </div>
                          <div className="flex items-center gap-1 text-yellow-400" title="TBD" data-id="element-499" data-mp="dyxjshf72nd2">
                            <HelpCircleIcon className="w-3.5 h-3.5" data-id="element-500" data-mp="2pk6hwg0ars1b" />
                            {member.tbd}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right text-gray-300" data-id="element-501" data-mp="2gji3vlawlrof">
                        {member.avgTime}
                      </td>
                      <td className="px-6 py-4" data-id="element-502" data-mp="38c8yjrcxv8bk">
                        <span className={`text-sm ${member.status === 'warning' ? 'text-amber' : 'text-gray-400'}`} data-id="element-503" data-mp="38dk7s6p5l8a2">
                          {member.lastActive}
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </Card>

          {/* 3. Quality Signals */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-504" data-mp="1svqqeedmn8lk">
            <Card className="p-6 flex flex-col items-center text-center justify-center" data-id="element-505" data-mp="1sxbi7w8k4dfq">
              <div className="w-12 h-12 rounded-full bg-amber/10 flex items-center justify-center mb-4 text-amber" data-id="element-506" data-mp="17sqkcc8xonof">
                <HelpCircleIcon className="w-6 h-6" data-id="element-507" data-mp="1bvwnhb0r1yqo" />
              </div>
              <div className="text-3xl font-serif text-white mb-1" data-id="element-508" data-mp="y1w0fej5u065">
                {qualitySignals.avgTbdRate}
              </div>
              <div className="text-sm font-medium text-gray-400 uppercase tracking-wider" data-id="element-509" data-mp="12z5n4mf5zxqa">
                Avg TBD Rate
              </div>
              <p className="text-xs text-gray-500 mt-2" data-id="element-510" data-mp="t3kp0zd7vhlt">
                Team average. High rates may indicate unclear review guidelines.
              </p>
            </Card>

            <Card className="p-6 flex flex-col items-center text-center justify-center" data-id="element-511" data-mp="k2csomu9gqud">
              <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center mb-4 text-blue-400" data-id="element-512" data-mp="29hkmm3olav5w">
                <MessageSquareIcon className="w-6 h-6" data-id="element-513" data-mp="2eh6zud4f4bep" />
              </div>
              <div className="text-3xl font-serif text-white mb-1" data-id="element-514" data-mp="2oklre0bvf9iu">
                {qualitySignals.totalComments}
              </div>
              <div className="text-sm font-medium text-gray-400 uppercase tracking-wider" data-id="element-515" data-mp="2jmajvw91tb3s">
                Total Comments
              </div>
              <p className="text-xs text-gray-500 mt-2" data-id="element-516" data-mp="2xf0fmsedpue3">
                Notes left by reviewers on documents.
              </p>
            </Card>

            <Card className="p-6 flex flex-col items-center text-center justify-center border-amber/30" data-id="element-517" data-mp="37aaj7kkfe9tz">
              <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mb-4 text-red-400" data-id="element-518" data-mp="32csa9u6erulz">
                <ShieldIcon className="w-6 h-6" data-id="element-519" data-mp="3gb8cp8x8myyb" />
              </div>
              <div className="text-3xl font-serif text-white mb-1" data-id="element-520" data-mp="3c9ww1swcdo29">
                {qualitySignals.privileged}
              </div>
              <div className="text-sm font-medium text-gray-400 uppercase tracking-wider" data-id="element-521" data-mp="1ly925u7psuxw">
                Privileged Docs
              </div>
              <p className="text-xs text-gray-500 mt-2" data-id="element-522" data-mp="3h0x50k5btit1">
                Documents tagged as privileged requiring partner review.
              </p>
            </Card>
          </div>
        </div>
      </main>
    </div>;
}